import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Batiment, Intervention, Personnel, Vehicule } from '../../../../shared/types/game';
import { RootState } from '../../../../core/store/store';
import { statuts, type StatutPlanningRowBase } from '../../../../config';
import { getPlanningServiceKeyFromBuildingType } from '../../utils/buildingServiceMapping';
import { DEFAULT_PLANNING_NOM, getPlanningLabelForLocalDate } from '../OperationalPlanningModal';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { useLanguage } from '../../../../shared/contexts/LanguageContext';
import { isTerminalInterventionStatus } from '../interventions/instanceModel';
import InterventionManageModal from '../interventions/InterventionManageModal';
import { useInterventionManageController } from '../interventions/useInterventionManageController';
import { useBuildingDetailsData } from './useBuildingDetailsData';
import BuildingPersonnelPlanningSection from './BuildingPersonnelPlanningSection';
import {
  sortPersonnelInGroup,
  comparePlanningGroupsByPrio,
  resolveCtaInstanceIdForIntervention,
  isPlanningPersonnelsJour,
} from './buildingDetailsTabHelpers';

interface BuildingDetailsTabProps {
  building: Batiment;
  personnel: Personnel[];
  vehicules: Vehicule[];
  calculatedLevel: number;
  totalCapacity: number;
  totalVehiclesCapacity: number;
}

const BuildingDetailsTab: React.FC<BuildingDetailsTabProps> = ({
  building,
  personnel,
  vehicules,
  calculatedLevel: _calculatedLevel,
  totalCapacity,
  totalVehiclesCapacity,
}) => {
  void _calculatedLevel;
  void totalCapacity;
  void totalVehiclesCapacity;

  const { t } = usePageTranslation('game', 'Map');
  const { language } = useLanguage();
  const titleLocale = language === 'en' ? 'en-US' : 'fr-FR';
  const { currentUser } = useSelector((state: RootState) => state.user);
  const userId = currentUser?.id;

  const {
    interventions,
    setInterventions,
    ctaInstances,
    setCtaInstances,
    ctaLoading,
    socketLive,
    priseAppels,
    priseLoadError,
    priseToggling,
    togglePriseAppels,
    authHeaders,
    loadCtaInstances,
    refreshCtaInstances,
    reloadBuildingInterventions,
  } = useBuildingDetailsData({ userId, t: t as (key: string) => string });

  const planningServiceKey = useMemo(() => getPlanningServiceKeyFromBuildingType(building.type), [building.type]);

  const mc = useInterventionManageController({
    userId,
    instances: ctaInstances,
    setInstances: setCtaInstances,
    loadInstances: loadCtaInstances,
    refreshInstances: refreshCtaInstances,
    authHeaders,
    t,
    titleLocale,
    fetchVehiclesEnabled: true,
  });


  const interventionsParVehicule = useMemo(() => {
    const countMap = new Map<number, number>();
    interventions.forEach((intervention) => {
      if (intervention.vehicules_ids && Array.isArray(intervention.vehicules_ids)) {
        intervention.vehicules_ids.forEach((vehiculeId) => {
          countMap.set(vehiculeId, (countMap.get(vehiculeId) || 0) + 1);
        });
      }
    });
    return countMap;
  }, [interventions]);

  const vehiculeIdsDuBatiment = useMemo(() => new Set(vehicules.map((v) => Number(v.id))), [vehicules]);
  const personnelIdsDuBatiment = useMemo(() => new Set(personnel.map((p) => Number(p.id))), [personnel]);

  /** Interventions encore « actives » pour ce bâtiment (ou moyens de ce bâtiment engagés). */
  const interventionsActivesPourBatiment = useMemo(() => {
    const bid = Number(building.id);
    return interventions.filter((i) => {
      if (i.statut !== 'en_cours' && i.statut !== 'en_attente') return false;
      const ib = i.batiment_id != null ? Number(i.batiment_id) : null;
      if (ib === bid) return true;
      const vHits = i.vehicules_ids?.some((vid) => vehiculeIdsDuBatiment.has(Number(vid))) ?? false;
      const pHits = i.personnel_ids?.some((pid) => personnelIdsDuBatiment.has(Number(pid))) ?? false;
      if (ib == null && (vHits || pHits)) return true;
      return false;
    });
  }, [interventions, building.id, vehiculeIdsDuBatiment, personnelIdsDuBatiment]);

  const moyensVehiculesIntervention = useMemo(() => {
    const ids = new Set<number>();
    interventionsActivesPourBatiment.forEach((i) => {
      i.vehicules_ids?.forEach((vid) => {
        const n = Number(vid);
        if (vehiculeIdsDuBatiment.has(n)) ids.add(n);
      });
    });
    return vehicules.filter((v) => ids.has(Number(v.id)));
  }, [interventionsActivesPourBatiment, vehiculeIdsDuBatiment, vehicules]);

  const moyensPersonnelIntervention = useMemo(() => {
    const ids = new Set<number>();
    interventionsActivesPourBatiment.forEach((i) => {
      i.personnel_ids?.forEach((pid) => {
        const n = Number(pid);
        if (personnelIdsDuBatiment.has(n)) ids.add(n);
      });
    });
    return personnel.filter((p) => ids.has(Number(p.id)));
  }, [interventionsActivesPourBatiment, personnelIdsDuBatiment, personnel]);

  const { personnelsDisponibles, personnelsDispoDepart } = useMemo(() => {
    const today = new Date();
    const actifs = personnel.filter((p) => p.actif);
    return {
      personnelsDisponibles: actifs.length,
      personnelsDispoDepart: actifs.filter((p) => !isPlanningPersonnelsJour(p, today)).length,
    };
  }, [personnel]);

  const personnelParPlanning = useMemo(() => {
    const today = new Date();
    const m = new Map<string, Personnel[]>();
    for (const p of personnel) {
      const key =
        String(getPlanningLabelForLocalDate(p, today) || DEFAULT_PLANNING_NOM).trim() || DEFAULT_PLANNING_NOM;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    }
    return Array.from(m.entries())
      .map(([k, v]) => [k, sortPersonnelInGroup(v)] as const)
      .sort(([a], [b]) => comparePlanningGroupsByPrio(a, b, planningServiceKey));
  }, [personnel, planningServiceKey]);

  const vehiculeDispoPourDepart = (v: Vehicule) => v.statut === 'disponible';

  const resolveManageId = useCallback(
    (inter: Intervention) =>
      userId
        ? resolveCtaInstanceIdForIntervention(inter, ctaInstances, vehiculeIdsDuBatiment)
        : null,
    [userId, ctaInstances, vehiculeIdsDuBatiment]
  );

  const renderInterventionManageButton = (inter: Intervention) => {
    const ctaId = resolveManageId(inter);
    if (!userId || ctaId == null) return null;
    return (
      <button
        type="button"
        onClick={() => mc.setManageId(ctaId)}
        className="ml-2 shrink-0 rounded-md border border-emerald-500/70 bg-emerald-600/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-200 hover:bg-emerald-600/40"
      >
        {t('interventionSidebar.manage')}
      </button>
    );
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-zinc-700/90 bg-gradient-to-b from-zinc-900/95 to-zinc-950 text-zinc-100 shadow-xl shadow-black/40 selection:bg-emerald-500/25">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-[0.2]"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/img_fond.jpg)`,
        }}
      />
      <div className="relative z-10 p-4 sm:p-5">
      <div className="mb-4 h-1 w-full rounded-full bg-gradient-to-r from-emerald-600 via-red-600 to-emerald-600 opacity-90" aria-hidden />

      {mc.actionError ? (
        <div className="mb-4 rounded-lg border border-red-500/40 bg-red-950/45 px-3 py-2 text-xs text-red-200">
          {mc.actionError}
        </div>
      ) : null}

      <section className="mb-5 overflow-hidden rounded-xl border border-zinc-700/80 bg-zinc-900/70 p-5 shadow-lg shadow-black/25">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-zinc-800 pb-3">
          <div className="flex items-center gap-2">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-amber-500/35 bg-amber-500/15 text-amber-300"
              aria-hidden
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </span>
            <h2 className="text-base font-extrabold uppercase tracking-wide text-emerald-300/95">
              Moyens en intervention
            </h2>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {userId && socketLive ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-600/50 bg-emerald-950/50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-300"
                title={t('interventionSidebar.synopticLiveHint')}
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-35" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                {t('interventionSidebar.synopticLive')}
              </span>
            ) : null}
            {userId && ctaLoading ? (
              <span className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">CTA…</span>
            ) : null}
          </div>
        </div>

        {userId ? (
          <div className="mb-4 w-full max-w-xl">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-zinc-700/80 bg-gradient-to-b from-zinc-900/90 to-zinc-950/90 px-2.5 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-zinc-200">{t('interventionSidebar.priseShort')}</p>
                <p className="text-[10px] text-zinc-500 leading-snug truncate">
                  {priseAppels ? t('interventionSidebar.priseOnHint') : t('interventionSidebar.priseOffHint')}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={priseAppels}
                disabled={priseToggling}
                onClick={() => void togglePriseAppels()}
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:opacity-50 ${
                  priseAppels ? 'bg-emerald-600' : 'bg-zinc-600'
                }`}
              >
                <span
                  className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${
                    priseAppels ? 'left-6' : 'left-1'
                  }`}
                />
              </button>
            </div>
            {priseLoadError ? (
              <p className="mt-1.5 text-[10px] text-red-400">{priseLoadError}</p>
            ) : null}
          </div>
        ) : null}

        {interventionsActivesPourBatiment.length === 0 ? (
          <p className="rounded-lg border border-dashed border-zinc-600 bg-zinc-950/40 py-8 text-center text-sm text-zinc-500">
            Aucun moyen en intervention
          </p>
        ) : moyensVehiculesIntervention.length === 0 && moyensPersonnelIntervention.length === 0 ? (
          <ul className="space-y-2 rounded-lg border border-amber-500/25 bg-amber-950/25 px-4 py-3 text-sm text-zinc-200">
            {interventionsActivesPourBatiment.map((inter) => (
              <li key={inter.id} className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="font-semibold text-white">{inter.type}</span>
                  {inter.localisation ? (
                    <span className="text-zinc-400"> · {inter.localisation}</span>
                  ) : null}
                  <span
                    className={`ml-2 text-xs font-medium uppercase ${
                      inter.statut === 'en_cours' ? 'text-sky-300' : 'text-amber-300'
                    }`}
                  >
                    {inter.statut === 'en_cours' ? 'En cours' : 'En attente'}
                  </span>
                </div>
                {renderInterventionManageButton(inter)}
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-3">
            <ul className="space-y-1.5 rounded-lg border border-zinc-800 bg-zinc-950/35 px-3 py-2">
              {interventionsActivesPourBatiment.map((inter) => (
                <li
                  key={`hdr-${inter.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-zinc-400"
                >
                  <span className="font-medium text-zinc-300">
                    {inter.type}
                    <span
                      className={`ml-2 uppercase ${
                        inter.statut === 'en_cours' ? 'text-sky-400' : 'text-amber-400'
                      }`}
                    >
                      {inter.statut === 'en_cours' ? 'en cours' : 'en attente'}
                    </span>
                  </span>
                  {renderInterventionManageButton(inter)}
                </li>
              ))}
            </ul>
            {moyensVehiculesIntervention.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {moyensVehiculesIntervention.map((v) => (
                  <div
                    key={v.id}
                    className="min-w-[6.5rem] max-w-[14rem] rounded-lg bg-gradient-to-b from-amber-600 to-orange-700 px-3 py-2.5 text-center text-xs font-bold uppercase tracking-wide text-white shadow-md ring-1 ring-orange-900/30"
                    title="Engin engagé sur une intervention en cours"
                  >
                    {v.nom}
                  </div>
                ))}
              </div>
            ) : null}
            {moyensPersonnelIntervention.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {moyensPersonnelIntervention.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg bg-gradient-to-b from-amber-600 to-orange-700 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white shadow-md ring-1 ring-orange-900/30"
                    title="Personnel engagé sur une intervention en cours"
                  >
                    {p.prenom} {p.nom}
                    <span className="mt-0.5 block text-[0.65rem] font-semibold normal-case text-white/90">
                      {p.grade}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section className="mb-5 rounded-xl border border-zinc-700/80 bg-zinc-900/70 p-5 shadow-lg shadow-black/25">
        <div className="mb-3 flex items-center gap-2">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-500/35 bg-emerald-500/10 text-emerald-300"
            aria-hidden
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12 0a2.25 2.25 0 01-2.25 2.25h-13.5a2.25 2.25 0 01-2.25-2.25v-7.5a2.25 2.25 0 012.25-2.25h13.5a2.25 2.25 0 012.25 2.25v7.5z"
              />
            </svg>
          </span>
          <h2 className="text-base font-extrabold uppercase tracking-wide text-emerald-300/95">Engins</h2>
        </div>

        {vehicules.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-500">Aucun engin assigné à ce bâtiment.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {vehicules.map((v) => {
              const nbInt = interventionsParVehicule.get(v.id) || 0;
              const dispo = vehiculeDispoPourDepart(v);
              const suffixMaint = v.statut === 'en_maintenance' ? ' (M)' : '';

              return (
                <div
                  key={v.id}
                  className={`relative min-w-[6.5rem] max-w-[12rem] rounded-lg px-3 py-2.5 text-center shadow-md ${
                    dispo
                      ? 'bg-gradient-to-b from-emerald-600 to-emerald-700 text-white ring-1 ring-emerald-500/35'
                      : 'bg-gradient-to-b from-zinc-600 to-zinc-700 text-zinc-100 ring-1 ring-zinc-800/50'
                  }`}
                >
                  {nbInt > 0 ? (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white ring-2 ring-zinc-900">
                      {nbInt}
                    </span>
                  ) : null}
                  <span className="block text-xs font-bold uppercase leading-tight tracking-wide">
                    {v.nom}
                    {suffixMaint}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <BuildingPersonnelPlanningSection
        personnel={personnel}
        personnelsDisponibles={personnelsDisponibles}
        personnelsDispoDepart={personnelsDispoDepart}
        personnelParPlanning={personnelParPlanning}
        planningServiceKey={planningServiceKey}
      />
      </div>

      <InterventionManageModal
        manageId={mc.manageId}
        onClose={() => mc.setManageId(null)}
        instances={ctaInstances}
        availableVehicles={mc.availableVehicles}
        selectedVehIdsByInstance={mc.selectedVehIdsByInstance}
        toggleSectorVehicle={mc.toggleSectorVehicle}
        selectAllSectorVehicles={mc.selectAllSectorVehicles}
        clearSectorSelection={mc.clearSectorSelection}
        sectorVehicles={mc.sectorVehicles}
        sectorAlreadyEngaged={mc.sectorAlreadyEngaged}
        sectorLoading={mc.sectorLoading}
        sectorError={mc.sectorError}
        busyId={mc.busyId}
        assignVehicle={mc.assignVehicle}
        advance={mc.advance}
        unassignReservedVehicle={mc.unassignReservedVehicle}
        recallInterventionVehicles={mc.recallInterventionVehicles}
        recallInterventionVehicle={mc.recallInterventionVehicle}
        reengageInterventionVehicle={mc.reengageInterventionVehicle}
        assignSectorVehicle={mc.assignSectorVehicle}
        handleDeploy={mc.handleDeploy}
        manageCompletion={mc.manageCompletion}
        titleLocale={mc.titleLocale}
      />
    </div>
  );
};

export default BuildingDetailsTab;
